import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import Layout from './Layout';
import Spinner from '../common/Spinner';
import RequireAuth from './RequireAuth';
import PublicOnly from './PublicOnly';
import { routes } from '@/routes';
import About from '@/pages/about';
import GanttEventsCalendar from '../events/GanttEventsCalendar';
import { useAuth } from '@/contexts/useAuth';
import Communities from '../communities/Communities';

const Home = lazy(() => import('@/pages/home'));
const Result = lazy(() => import('@/pages/donate/result'));
const Post = lazy(() => import('@/pages/post'));
const EditPost = lazy(() => import('@/pages/post/edit'));
const CreatePost = lazy(() => import('@/pages/create-post'));
const Profile = lazy(() => import('@/pages/user'));
const Activity = lazy(() => import('@/pages/activity'));
const EventDetails = lazy(() => import('@/pages/event'));
const SignIn = lazy(() => import('@/pages/login'));
const SignUp = lazy(() => import('@/pages/signup'));
const ForgotPassword = lazy(() => import('@/pages/forgot-password'));
const ResetPassword = lazy(() => import('@/pages/reset-password'));
const DonatePage = lazy(() => import('@/pages/donate'));
const AdminDashboard = lazy(() => import('@/pages/admin'));
const FeedbackPage = lazy(() => import('@/pages/feedback'));
const NotificationsPage = lazy(() => import('@/pages/notifications'));
const SearchPage = lazy(() => import('@/pages/search'));
const ComponentPreviewPage = lazy(() => import('@/pages/dev/components'));
const Terms = lazy(() => import('@/pages/terms'));

const CreateEvent = lazy(() => import('@/components/events/CreateEvent'));
const Leaderboard = lazy(() => import('@/components/Leaderboard'));
const Chat = lazy(() => import('@/components/messages/Chat'));

const LegacySignUpRedirect = () => {
    const location = useLocation();

    return <Navigate to={`${routes.signUp}${location.search}`} replace />;
};

const HomeOrAbout = () => {
    const { loading } = useAuth();

    // Show spinner while loading auth state
    if (loading) return <Spinner text='Loading...' variant='fullscreen' />;

    // Show home feed for all users (logged in or not)
    return <Home />;
};

function AppNavigator() {
    return (
        <Suspense
            fallback={<Spinner text='Loading app...' variant='fullscreen' />}
        >
            <Routes>
                <Route path='' element={<Layout />}>
                    <Route path={routes.about} element={<About />} />
                    <Route path={routes.terms} element={<Terms />} />
                    <Route
                        path='/dev/components'
                        element={<ComponentPreviewPage />}
                    />
                    <Route path={routes.home} element={<HomeOrAbout />} />
                    <Route path={routes.result} element={<Result />} />
                    <Route path={routes.donate} element={<DonatePage />} />
                    <Route
                        path={routes.search}
                        element={
                            <RequireAuth>
                                <SearchPage />
                            </RequireAuth>
                        }
                    />

                    <Route path='/post/:id' element={<Post />} />

                    <Route
                        path='/post/:id/edit'
                        element={
                            <RequireAuth>
                                <EditPost />
                            </RequireAuth>
                        }
                    />

                    <Route
                        path='/post/:id/edit'
                        element={
                            <RequireAuth>
                                <EditPost />
                            </RequireAuth>
                        }
                    />

                    <Route
                        path={routes.createPost}
                        element={
                            <RequireAuth>
                                <CreatePost />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path='/user/:id'
                        element={
                            <RequireAuth>
                                <Profile />
                            </RequireAuth>
                        }
                    />
                    <Route path='/event/:id' element={<EventDetails />} />
                    <Route
                        path={routes.events}
                        element={
                            <RequireAuth>
                                <GanttEventsCalendar />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.createEvent}
                        element={
                            <RequireAuth>
                                <CreateEvent />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.chat}
                        element={
                            <RequireAuth>
                                <Chat channelType='public' />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.dms}
                        element={
                            <RequireAuth>
                                <Chat channelType='dm' />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path='/dms/:id?'
                        element={
                            <RequireAuth>
                                <Chat channelType='dm' />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.communities}
                        element={
                            <RequireAuth>
                                <Communities />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.leaderboard}
                        element={
                            <RequireAuth>
                                <Leaderboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.feedback}
                        element={
                            <RequireAuth>
                                <FeedbackPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.notifications}
                        element={
                            <RequireAuth>
                                <NotificationsPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.activity}
                        element={
                            <RequireAuth>
                                <Activity />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={routes.admin}
                        element={
                            <RequireAuth
                                allow={({ isLoggedIn, isAdmin }) =>
                                    isLoggedIn && isAdmin
                                }
                            >
                                <AdminDashboard />
                            </RequireAuth>
                        }
                    />

                    <Route
                        path={routes.login}
                        element={
                            <PublicOnly>
                                <SignIn />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path={routes.signUp}
                        element={
                            <PublicOnly>
                                <SignUp />
                            </PublicOnly>
                        }
                    />
                    <Route path='/sign-up' element={<LegacySignUpRedirect />} />
                    <Route
                        path={routes.forgotPassword}
                        element={
                            <PublicOnly>
                                <ForgotPassword />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path={routes.resetPassword}
                        element={
                            <PublicOnly>
                                <ResetPassword />
                            </PublicOnly>
                        }
                    />

                    <Route
                        path='*'
                        element={<Navigate to={routes.home} replace />}
                    />
                </Route>
            </Routes>
        </Suspense>
    );
}

export default AppNavigator;
